/** Long enough for a large photo on a slow line, short enough that a dead host does not hold the check for minutes. */
const LOAD_TIMEOUT_MS = 15000;

/** How often a loading image is asked for its size, which it knows once its header arrives. */
const SIZE_POLL_MS = 50;

/** About as many requests as a browser sends to one site at once. */
const CONCURRENT_LOADS = 6;

/** Often enough to look live, rarely enough that hundreds of dead links do not redraw a long table hundreds of times. */
const REPORT_EVERY_MS = 250;

export type LoadImage = (url: string) => Promise<boolean>;

/**
 * Whether the browser gets an image from the URL. A request to another site
 * cannot read its answer without that site's CORS headers, while an image
 * element loads from anywhere and only learns a size from something it can
 * decode. No referrer, as the host downloads it with none.
 */
export const loadImage: LoadImage = url =>
    new Promise(resolve => {
        const image = new Image();
        const settle = (loaded: boolean) => {
            window.clearTimeout(timer);
            window.clearInterval(poll);
            image.onload = image.onerror = null;
            // Knowing it is an image is enough: the rest of a large photo is not downloaded.
            image.removeAttribute('src');
            resolve(loaded);
        };
        const timer = window.setTimeout(() => settle(false), LOAD_TIMEOUT_MS);
        const poll = window.setInterval(() => image.naturalWidth > 0 && settle(true), SIZE_POLL_MS);

        image.referrerPolicy = 'no-referrer';
        image.onload = () => settle(true);
        image.onerror = () => settle(false);
        image.src = url;
    });

/** Reports the URLs whose image does not load, each once however many rows name it, a few at a time. */
export async function checkImages(
    urls: readonly string[],
    onUnreachable: (urls: string[]) => void,
    { signal, load = loadImage }: { signal?: AbortSignal; load?: LoadImage } = {}
): Promise<void> {
    const pending = [...new Set(urls)];
    let found: string[] = [];
    const report = () => {
        if (found.length === 0 || signal?.aborted) return;
        onUnreachable(found);
        found = [];
    };
    const work = async () => {
        for (let url = pending.shift(); url !== undefined && !signal?.aborted; url = pending.shift()) {
            if (!(await load(url))) found.push(url);
        }
    };
    const reporting = window.setInterval(report, REPORT_EVERY_MS);

    try {
        await Promise.all(Array.from({ length: CONCURRENT_LOADS }, work));
    } finally {
        window.clearInterval(reporting);
        report();
    }
}
